import type { TFunction } from 'i18next';
import type { MemoryReadRollupRow } from '../services/memoryReadSummary';

/** Normalize API operation string for nested i18n keys (dots are invalid in flat key segments). */
export function operationToKey(operation: string): string {
  return (operation || 'unknown').replace(/\./g, '_');
}

function titleCaseToken(token: string): string {
  if (!token) return token;
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

/** Readable fallback when no translation exists (unknown backend value). */
export function humanizeIdentifier(value: string): string {
  const raw = (value || '').trim() || 'unknown';
  return raw
    .split(/[._]+/)
    .filter(Boolean)
    .map(titleCaseToken)
    .join(' · ');
}

export interface SegmentLabels {
  operationLabel: string;
  integrationLabel: string;
  outcomeLabel: string;
}

/**
 * Resolve display labels for a rollup row. Uses `dashboard` namespace keys under
 * `memoryReadHealth.segments.*` (caller should use `t` with keyPrefix `memoryReadHealth`).
 */
export function getSegmentLabels(
  /** Scoped with `keyPrefix: 'memoryReadHealth'` on the `dashboard` namespace. */
  t: TFunction,
  row: Pick<MemoryReadRollupRow, 'operation' | 'integration_type' | 'outcome'>
): SegmentLabels {
  const opKey = operationToKey(row.operation);
  const intKey = (row.integration_type || 'unknown').replace(/\./g, '_');
  const outKey = (row.outcome || 'unknown').replace(/\./g, '_');

  return {
    operationLabel: t(`segments.operation.${opKey}`, {
      defaultValue: humanizeIdentifier(row.operation),
    }),
    integrationLabel: t(`segments.integration.${intKey}`, {
      defaultValue: humanizeIdentifier(row.integration_type),
    }),
    outcomeLabel: t(`segments.outcome.${outKey}`, {
      defaultValue: humanizeIdentifier(row.outcome),
    }),
  };
}

/**
 * Short axis label for a rollup day (`YYYY-MM-DD`). Uses UTC date parts to match server buckets.
 */
export function formatRollupDayLabel(isoDate: string, locale: string): string {
  const trimmed = isoDate.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!m) return trimmed;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo, d, 12, 0, 0));
  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(dt);
  } catch {
    return new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(dt);
  }
}
